import React from 'react';
import { Briefcase, Search, Filter, X, ArrowRight } from 'lucide-react';

interface JobCategory {
  code: number;
  name: string;
  nJobs: string;
}

interface JobFiltersProps {
  categories: JobCategory[];
  onFilterChange: (category: number | null) => void;
  selectedCategory: number | null;
  isLoading?: boolean;
}

export function JobFilters({ categories, onFilterChange, selectedCategory, isLoading = false }: JobFiltersProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  
  // Sort categories by name
  const sortedCategories = React.useMemo(() => {
    if (!categories || !Array.isArray(categories)) return [];
    
    return [...categories].sort((a, b) => 
      a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })
    );
  }, [categories]);
  
  // Filter categories by search term
  const filteredCategories = React.useMemo(() => {
    if (!searchTerm.trim() || !sortedCategories.length) return sortedCategories;
    
    const searchLower = searchTerm.toLowerCase().trim();
    return sortedCategories.filter(category => 
      category.name.toLowerCase().includes(searchLower)
    );
  }, [sortedCategories, searchTerm]);

  // Extract the category name without the job count
  const getCategoryName = (name: string): string => {
    return name.replace(/\s*\(\d+\)$/, '');
  };

  return (
    <div className="mb-6">
      {/* Mobile filter button */}
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden flex items-center justify-between w-full p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm hover:shadow-md transition-all duration-200 mb-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
            <Filter className="w-5 h-5 text-primary-500" />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Filtrer par secteur
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {selectedCategory ? 
                categories.find(c => c.code === selectedCategory)?.name || 'Sélectionné' : 
                'Tous les secteurs'}
            </p>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400" />
      </button>

      {/* Desktop filter sidebar */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary-500" />
            <span>Secteurs d'activité</span>
          </h3>
          {selectedCategory && (
            <button
              onClick={() => onFilterChange(null)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Réinitialiser
            </button>
          )}
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input 
            type="text" 
            placeholder="Rechercher un secteur..." 
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="h-[500px] space-y-2 animate-pulse">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
            ))}
          </div>
        ) : (
          <div className="h-[500px] overflow-y-auto pr-1 custom-scrollbar">
            <div className="space-y-1">
              <button
                onClick={() => onFilterChange(null)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${
                  selectedCategory === null 
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                }`}
              >
                <span>Tous les secteurs</span>
                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
                  {categories.reduce((acc, cat) => acc + parseInt(cat.nJobs || '0'), 0)}
                </span>
              </button>
              
              {filteredCategories.map((category) => (
                <button
                  key={category.code}
                  onClick={() => onFilterChange(category.code)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${
                    selectedCategory === category.code 
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span className="truncate">{getCategoryName(category.name)}</span>
                  <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg flex-shrink-0">
                    {category.nJobs}
                  </span>
                </button>
              ))}

              {filteredCategories.length === 0 && (
                <div className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                  <p>Aucun secteur trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile filter modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden">
          <div className="absolute inset-x-0 bottom-0 max-h-[90vh] bg-white dark:bg-gray-800 rounded-t-2xl overflow-hidden shadow-xl">
            <div className="sticky top-0 flex items-center justify-between p-4 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm z-10 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Secteurs d'activité
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Rechercher un secteur..." 
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            
              {isLoading ? (
                <div className="space-y-2 animate-pulse">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-14 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
                  ))}
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar">
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        onFilterChange(null);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left ${
                        selectedCategory === null 
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                          : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <span>Tous les secteurs</span>
                      <span className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded-lg">
                        {categories.reduce((acc, cat) => acc + parseInt(cat.nJobs || '0'), 0)}
                      </span>
                    </button>
                    
                    {filteredCategories.map((category) => (
                      <button
                        key={category.code}
                        onClick={() => {
                          onFilterChange(category.code);
                          setIsOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left ${
                          selectedCategory === category.code 
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                            : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="truncate">{getCategoryName(category.name)}</span>
                        <span className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded-lg flex-shrink-0">
                          {category.nJobs}
                        </span>
                      </button>
                    ))}

                    {filteredCategories.length === 0 && (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-xl">
                        <p>Aucun secteur trouvé</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="sticky bottom-0 p-4 border-t dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    onFilterChange(null);
                    setIsOpen(false);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm hover:shadow-md transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}